import { Fragment, useState } from "react";
import { api, type FormatRaportu } from "../../lib/api.js";
import { bladPobraniaRaportu } from "../../lib/raport.js";
import type { Mozliwosc } from "../../lib/raportOceny.js";
import { zapiszBlobJakoPlik } from "../../lib/zapiszPlik.js";
import { IconPobierz } from "../icons/index.js";
import { Button } from "../ui/Button.js";
import { useToast } from "../ui/Toast.js";
import { Tooltip } from "../ui/Tooltip.js";

/**
 * "Pobierz raport (.docx)" i "Pobierz raport (.pdf)" w naglowku karty oceny (TB-9: secondary z ikona pobierania, stan
 * ladowania, toast po gotowym pliku). Word jest do dalszej pracy, PDF do czytania i wysylania. Oba pliki obejmuja
 * WSZYSTKICH mowcow, niezaleznie od mowcy wybranego na ekranie: mowi o tym zdanie w karcie, nie sam przycisk.
 *
 * Kazdy format ma wlasny stan ladowania i pobiera sie niezaleznie (zadne z nich nie blokuje drugiego, wiec zaden
 * przycisk nie jest wygaszony bez powodu, S-10).
 *
 * Oba wygaszone z powodem (S-10), gdy serwer i tak by odmowil: ocena w toku, czekanie na podzial, brak wynikow.
 */

const FORMATY: readonly { format: FormatRaportu; etykieta: string }[] = [
  { format: "docx", etykieta: "Pobierz raport (.docx)" },
  { format: "pdf", etykieta: "Pobierz raport (.pdf)" },
];

export function PrzyciskRaportu({
  sesjaId,
  mozliwosc,
  wariant = "administrator",
}: {
  sesjaId: string;
  mozliwosc: Mozliwosc;
  /** Widok uzytkownika: bledy pobrania bez kodow w nawiasach i w jego jezyku ("rozmowa", a nie "analiza"). */
  wariant?: "administrator" | "uzytkownik";
}): React.JSX.Element {
  const toast = useToast();
  const [trwa, setTrwa] = useState<Record<FormatRaportu, boolean>>({ docx: false, pdf: false });

  async function pobierz(format: FormatRaportu): Promise<void> {
    if (trwa[format] || !mozliwosc.mozna) return;
    setTrwa((s) => ({ ...s, [format]: true }));
    try {
      const { blob, nazwaPliku } = await api.pobierzRaport(sesjaId, format);
      zapiszBlobJakoPlik(blob, nazwaPliku);
      // TO-1: potwierdzenie ZDARZENIA (plik jest gotowy), nie stanu.
      toast.sukces("Raport pobrany.");
    } catch (err) {
      toast.blad(bladPobraniaRaportu(err, wariant));
    } finally {
      setTrwa((s) => ({ ...s, [format]: false }));
    }
  }

  return (
    <>
      {FORMATY.map(({ format, etykieta }) => {
        const przycisk = (
          <Button wariant="secondary" zajety={trwa[format]} wylaczony={!mozliwosc.mozna} onClick={() => void pobierz(format)}>
            {!trwa[format] && <IconPobierz />}
            {trwa[format] ? "Przygotowuję raport..." : etykieta}
          </Button>
        );
        return mozliwosc.mozna ? (
          <Fragment key={format}>{przycisk}</Fragment>
        ) : (
          <Tooltip key={format} tekst={mozliwosc.powod}>
            {przycisk}
          </Tooltip>
        );
      })}
    </>
  );
}
